import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useOutsideClick } from "../../hooks/useOutsideClick";
import { logEvent, logInteractionEvent } from "../../utils/appInsight";
import {
  trackAdobeAppClick,
  trackAdobeSocialClick,
} from "../../utils/adobeLogging";
import constants from "../../utils/constants";
import { useTranslation } from "react-i18next";
import { incrementSharesCountThunk } from "../../store/thunks";
import { toggleShareBadgeModal } from "../../store/actions";

const ShareBadgeModal = () => {
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);
  const section = useSelector((state) => state.progress.section);
  const lang = useSelector((state) => state.ui.lang);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const onClose = () => dispatch(toggleShareBadgeModal());

  const page =
    section === "PMAgile"
      ? "agile"
      : "predictive"


  const url = `${window.location.protocol}//${window.location.hostname}/${page}.html?${Math.random().toString(36).replace(/[^a-z]+/g, '').substr(0, 10)}`;

  
  const twitterUrl = `https://twitter.com/share?url=${encodeURIComponent(
    url
  )}`;
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
    url
  )}`;
  const linkedUrl = `https://www.linkedin.com/shareArticle?url=${encodeURIComponent(
    url
  )}`;

  const share = (socialUrl, network) => {
    const left = Math.round(window.screen.width / 2 - 640 / 2);
    const top = Math.round(window.screen.height / 2 - 480 / 2);
    const window_config = `width=${640},height=${480},left=${left},top=${top}`;

    dispatch(incrementSharesCountThunk());

    window.open(
      socialUrl,
      "Share this",
      `${window_config},toolbar=no,menubar=no,scrollbars=no`
    );

    logEvent({
      name: "BadgeShared",
      properties: {
        section,
        platform: network,
      },
    });
    logInteractionEvent("BadgeShared", {
      section,
      type: network,
    });

    trackAdobeAppClick({ action: network, language: lang });
    trackAdobeSocialClick({
      socialTool: network,
      linkTitle: network,
      linkModule: `${section}-share-badge-modal`,
      targetURL: socialUrl,
    });

    onClose();
  };

  const onCopy = () => {
    setCopied(true);
    dispatch(incrementSharesCountThunk());
    logEvent({
      name: "BadgeShared",
      properties: {
        section,
        platform: "url",
      },
    });
    logInteractionEvent("BadgeShared", {
      section,
      platform: "url",
    });
    trackAdobeAppClick({ action: 'copy-link', language: lang });
    trackAdobeSocialClick({
      socialTool: 'copy-link',
      linkTitle: 'copy-link',
      linkModule: `${section}-share-badge-modal`,
      targetURL: null,
    });
    // trackAdobeClick(null,{
    //   linkTitle: "copy",
    //   linkModule: `${section}-share-badge-modal`,
    //   targetURL: url,
    // })
  };

  useOutsideClick(ref, onClose);

  return (
    <div
      className="fixed z-10 inset-0 overflow-y-auto"
      adoberegion={`${section}-share-badge-modal`}
    >
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity">
          <div className="absolute inset-0 bg-gray-500 opacity-75" />
        </div>
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" />
        &#8203;
        <div
          className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-sm sm:w-full sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-headline"
        >
          <div ref={ref}>
            <div className="text-center font-bold pb-5">{t("shareTo")}</div>

            <div className="grid grid-cols-3 grid-rows-1 gap-4">
              <div
                className="cursor-pointer"
                onClick={() =>
                  share(linkedUrl, constants.socialPlatforms.linkedin)
                }
              >
                <div className="flex flex-col justify-center items-center">
                  <div>
                    <img
                      src="images/linkedin-icon.png"
                      alt="linkedin"
                      style={{ width: "48px" }}
                    />
                  </div>
                  <div>{t("linkedIn")}</div>
                </div>
              </div>
              <div
                className="cursor-pointer"
                onClick={() =>
                  share(facebookUrl, constants.socialPlatforms.facebook)
                }
              >
                <div className="flex flex-col justify-center items-center">
                  <div>
                    <img
                      src="images/facebook-icon.png"
                      alt="facebook"
                      style={{ width: "48px" }}
                    />
                  </div>
                  <div>{t("facebook")}</div>
                </div>
              </div>

              <div
                className="cursor-pointer"
                onClick={() =>
                  share(twitterUrl, constants.socialPlatforms.twitter)
                }
              >
                <div className="flex flex-col justify-center items-center">
                  <div>
                    <img
                      src="images/twitter-icon.png"
                      alt="twitter"
                      style={{ width: "48px" }}
                    />
                  </div>
                  <div>{t("twitter")}</div>
                </div>
              </div>
            </div>

            {copied && (
              <div className="text-sm mt-5 px-5 py-6 bg-w-2 text-white rounded">
                {t("linkCopied")} <br />
                {url}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareBadgeModal;
